/**
 * fitment.js — the one module in front of the EasySearch App Proxy.
 *
 * Every trailer / identifier lookup in the theme — the header typeahead, the results page,
 * the saved-trailer chip's weekly refresh — goes through `window.ValerydFitment`. The
 * callers keep only rendering; this module owns everything a caller would otherwise have
 * to know about the proxy:
 *
 *   - the endpoint (GET /apps/easysearch/search?regnr=…&country=…);
 *   - the `country` parameter, which picks the registry AND the label language and 401s on
 *     anything off its list, so the fallback is SE — the registry known to answer — never a
 *     neutral value (docs/easysearch-backend-delta.md §4);
 *   - normalisation of the query (spaces and dashes out, upper-case) — one convention, so
 *     the cache key, the wire value and the identifier comparison cannot disagree;
 *   - a 12 s ceiling on the proxy call. The SOAP chain behind the proxy is the slow part
 *     worth bounding; a proxy that accepts the connection and never answers must resolve to
 *     a service-error result, because silence tells the customer nothing;
 *   - a two-minute sessionStorage cache of positive matches, keyed by country and query, so
 *     the header lookup and the results page it navigates to cost one proxy call;
 *   - the shape of the normalised response — `articleLists` with `articleNumbers`/`artNos`,
 *     the flat `articleNumbers` fallback of an older backend, the identifier search types.
 *
 * Interface:
 *
 *   lookup(query, { country, signal }) → Promise<
 *       { ok: true,  data, cached }
 *     | { ok: false, kind: 'invalid' | 'unavailable' | 'error' | 'timeout' | 'aborted', status? } >
 *     Never rejects. 'invalid' is the proxy's 400 (bad input); 'unavailable' is 502/503 or a
 *     network failure; 'error' is any other non-2xx; 'timeout' is the 12 s ceiling; 'aborted'
 *     is the caller's own signal firing, which callers treat as "a newer query replaced me".
 *   normalise(query)               → 'UKB104' for ' ukb-104 '
 *   looksLikeIdentifier(query)     → Swedish (ABC12X / ABC123) and Norwegian (AB12345) plates,
 *                                    and a 17-character VIN (ISO 3779: no I, O or Q)
 *   looksLikePartIdentifier(query) → a brake ID, OE or article number or a brake dimension as
 *                                    typed: four characters or more with a digit, and neither a
 *                                    plate nor a VIN. Free text never qualifies.
 *   groups(data)                   → [{ searchTypeKey, searchTypeText, searchFor, articleNumbers }]
 *                                    in the backend's order, empty lists dropped
 *   skus(data)                     → every fitting article number, upper-cased, de-duplicated
 *   hasIdentifierMatch(data)       → a structured identifier matched (not just a part name)
 *   matchesIdentifier(data, query) → the trailer was found FOR this reg-no / VIN specifically
 *   describe(trailer, labels?)     → 'TGAB LV 1000 · 2003 · Braked' (labels: { braked, unbraked })
 *
 * The theme never sees SOAP, credentials or ranking — that is the middleware's job.
 */
(() => {
  if (window.ValerydFitment) return;

  const ENDPOINT = '/apps/easysearch/search';
  const DEFAULT_COUNTRY = 'SE';
  const PROXY_TIMEOUT_MS = 12000;
  const CACHE_PREFIX = 'valeryd:easysearch:';
  const CACHE_MAX_AGE_MS = 2 * 60 * 1000;
  // Structured identifiers — a match on one of these names the result and, on the results
  // page, takes over from the standard product grid. `vin` (added by the backend 2026-09)
  // behaves like `regno`: it identifies a trailer.
  const IDENTIFIER_TYPES = ['regno', 'vin', 'bromsid', 'oe', 'artno', 'brakedimmanuf'];
  const TRAILER_TYPES = ['regno', 'vin'];
  const IDENTIFIER_SHAPE = /^([A-Z]{3}[0-9]{2}[A-Z0-9]|[A-Z]{2}[0-9]{4,5}|[A-HJ-NPR-Z0-9]{17})$/;

  function normalise(value) {
    return String(value || '')
      .replace(/[\s-]/g, '')
      .toUpperCase();
  }

  function looksLikeIdentifier(query) {
    return IDENTIFIER_SHAPE.test(normalise(query));
  }

  function looksLikePartIdentifier(query) {
    const value = normalise(query);
    return value.length >= 4 && /[0-9]/.test(value) && !looksLikeIdentifier(value);
  }

  // ---------------------------------------------------------------- cache

  function cacheKey(query, country) {
    return CACHE_PREFIX + normalise(country || DEFAULT_COUNTRY) + ':' + normalise(query);
  }

  function storage() {
    try {
      return window.sessionStorage;
    } catch (error) {
      return null;
    }
  }

  function cacheGet(query, country) {
    const store = storage();
    if (!store) return null;
    const key = cacheKey(query, country);
    try {
      const entry = JSON.parse(store.getItem(key) || 'null');
      if (!entry || !entry.data || !Number.isFinite(entry.savedAt) || Date.now() - entry.savedAt > CACHE_MAX_AGE_MS) {
        store.removeItem(key);
        return null;
      }
      return entry.data;
    } catch (error) {
      try {
        store.removeItem(key);
      } catch (ignored) {
        /* treat as a miss */
      }
      return null;
    }
  }

  function cacheSet(query, country, data) {
    const store = storage();
    if (!store || !normalise(query) || !data) return;
    try {
      store.setItem(cacheKey(query, country), JSON.stringify({ data: data, savedAt: Date.now() }));
    } catch (error) {
      /* storage unavailable or full — the next page makes its normal request */
    }
  }

  // ---------------------------------------------------------------- response shape

  function groups(data) {
    if (data && Array.isArray(data.articleLists) && data.articleLists.length) {
      return data.articleLists
        .map((list) => {
          const nums = Array.isArray(list.articleNumbers) ? list.articleNumbers : Array.isArray(list.artNos) ? list.artNos : [];
          return {
            searchTypeKey: ((list.searchTypeKey || '') + '').trim().toLowerCase(),
            searchTypeText: ((list.searchTypeText || '') + '').trim(),
            searchFor: ((list.searchFor || list.inputSearchString || '') + '').trim(),
            articleNumbers: nums.filter(Boolean),
          };
        })
        .filter((group) => group.articleNumbers.length);
    }
    const flat = data && Array.isArray(data.articleNumbers) ? data.articleNumbers.filter(Boolean) : [];
    return flat.length ? [{ searchTypeKey: '', searchTypeText: '', searchFor: '', articleNumbers: flat }] : [];
  }

  function skus(data) {
    const seen = new Set();
    groups(data).forEach((group) => group.articleNumbers.forEach((sku) => seen.add((sku + '').trim().toUpperCase())));
    return Array.from(seen).filter(Boolean);
  }

  function hasIdentifierMatch(data) {
    const lists = data && Array.isArray(data.articleLists) ? data.articleLists : null;
    if (!lists) return true; // older backend without typed lists — keep "found → show"
    return groups(data).some((g) => IDENTIFIER_TYPES.indexOf(g.searchTypeKey) !== -1);
  }

  function matchesIdentifier(data, query) {
    if (!data || data.found === false || !data.trailer) return false;
    const lists = Array.isArray(data.articleLists) ? data.articleLists : [];
    if (!lists.length) return true;
    const wanted = normalise(query);
    return groups(data).some((g) => TRAILER_TYPES.indexOf(g.searchTypeKey) !== -1 && normalise(g.searchFor) === wanted);
  }

  function describe(trailer, labels) {
    if (!trailer) return '';
    labels = labels || {};
    const braked = labels.braked || labels.unbraked ? (trailer.unbraked ? labels.unbraked : labels.braked) : '';
    return [trailer.description, trailer.year, braked].filter(Boolean).join(' · ');
  }

  // ---------------------------------------------------------------- bot protection
  //
  // Every lookup carries a Cloudflare Turnstile token (`ts`), and the search backend refuses one
  // without a valid token. The reg-no index is the asset scraping would take: the App Proxy route
  // is public, and the proxy cannot rate-limit per visitor because Shopify hides the visitor's IP.
  // Turnstile runs invisibly and shows a challenge only when the visitor looks automated.
  // No site key on <html> (a store without the setting) = no token, and the backend decides.
  const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
  const TOKEN_TIMEOUT_MS = 10000;
  let turnstileReady = null;
  let widget = null;
  let pendingToken = null;

  function loadTurnstile() {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (turnstileReady) return turnstileReady;
    turnstileReady = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = TURNSTILE_SRC;
      s.async = true;
      s.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('turnstile missing')));
      s.onerror = () => reject(new Error('turnstile blocked'));
      document.head.appendChild(s);
    }).catch((e) => { turnstileReady = null; throw e; });
    return turnstileReady;
  }

  async function turnstileToken(sitekey) {
    try {
      const ts = await loadTurnstile();
      if (widget === null) {
        const box = document.createElement('div');
        box.className = 'fitment-turnstile';
        document.body.appendChild(box);
        widget = ts.render(box, {
          sitekey,
          execution: 'execute',
          appearance: 'interaction-only',
          callback: (token) => pendingToken && pendingToken.resolve(token),
          'error-callback': () => pendingToken && pendingToken.resolve(null),
          // A visible challenge waits for the visitor; the timeout only guards the invisible path.
          'before-interactive-callback': () => pendingToken && clearTimeout(pendingToken.timer),
        });
      } else {
        ts.reset(widget);
      }
      const got = new Promise((resolve) => { pendingToken = { resolve }; });
      pendingToken.timer = setTimeout(() => pendingToken.resolve(null), TOKEN_TIMEOUT_MS);
      ts.execute(widget);
      return await got;
    } catch (e) {
      return null;
    }
  }

  // ---------------------------------------------------------------- lookup

  async function lookup(query, opts) {
    opts = opts || {};
    const regnr = String(query || '').trim();
    const country = opts.country || DEFAULT_COUNTRY;
    if (!regnr) return { ok: false, kind: 'invalid' };

    const cached = cacheGet(regnr, country);
    if (cached) return { ok: true, data: cached, cached: true };

    // The token first: waiting for Turnstile must not eat the proxy's own timeout below. Without a
    // site key nothing is awaited, so the request leaves in the same tick as before.
    const sitekey = document.documentElement.getAttribute('data-turnstile-sitekey');
    const token = sitekey ? await turnstileToken(sitekey) : null;
    if (sitekey && opts.signal && opts.signal.aborted) return { ok: false, kind: 'aborted' };

    const controller = new AbortController();
    let timedOut = false;
    const onOuterAbort = () => controller.abort();
    if (opts.signal) {
      if (opts.signal.aborted) return { ok: false, kind: 'aborted' };
      opts.signal.addEventListener('abort', onOuterAbort, { once: true });
    }
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, PROXY_TIMEOUT_MS);

    // The backend's own address when the theme sets one (<html data-regnr-endpoint>): the relay
    // behind ENDPOINT replaces the visitor's IP, and the per-visitor limit needs it.
    const endpoint = document.documentElement.getAttribute('data-regnr-endpoint') || ENDPOINT;
    const url = endpoint + '?regnr=' + encodeURIComponent(regnr) + '&country=' + encodeURIComponent(country) +
      (token ? '&ts=' + encodeURIComponent(token) : '');
    try {
      const res = await fetch(url, { headers: { Accept: 'application/json' }, signal: controller.signal });
      // The App Proxy uses 400 (bad input) / 502 (EasySearch down) with a JSON { error } body.
      if (!res.ok) {
        if (res.status === 400) return { ok: false, kind: 'invalid', status: 400 };
        // 403 = the backend refused the Turnstile token (or none was sent): shown as a service error.
        if (res.status === 403) return { ok: false, kind: 'unavailable', status: 403 };
        if (res.status === 502 || res.status === 503) return { ok: false, kind: 'unavailable', status: res.status };
        return { ok: false, kind: 'error', status: res.status };
      }
      const data = await res.json();
      if (opts.signal && opts.signal.aborted) return { ok: false, kind: 'aborted' };
      // Only a positive trailer match is worth carrying to the next page; misses and
      // free-text hits are cheap to repeat and would otherwise pin a stale answer.
      if (data && data.found !== false && data.trailer) cacheSet(regnr, country, data);
      return { ok: true, data: data, cached: false };
    } catch (error) {
      // Order matters: the timeout aborts the controller too, so it is tested first.
      if (timedOut) return { ok: false, kind: 'timeout' };
      if ((opts.signal && opts.signal.aborted) || error.name === 'AbortError') return { ok: false, kind: 'aborted' };
      return { ok: false, kind: 'unavailable' };
    } finally {
      clearTimeout(timer);
      if (opts.signal) opts.signal.removeEventListener('abort', onOuterAbort);
    }
  }

  window.ValerydFitment = { lookup, normalise, looksLikeIdentifier, looksLikePartIdentifier, groups, skus, hasIdentifierMatch, matchesIdentifier, describe };
})();
